import React from "react";
import { Link } from "react-router-dom";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Check } from "lucide-react";
import { PACKAGES } from "@/lib/data";

export default function Packages() {
    return (
        <div className="pt-24 sm:pt-28">
            <Section spacing="tight">
                <Kicker>Packages</Kicker>
                <SerifTitle className="mt-3">A considered menu of experiences.</SerifTitle>
                <p className="mt-5 max-w-[64ch] text-sm leading-[1.85] text-muted-foreground">
                    Every engagement begins with a private conversation. Pricing begins at the figures listed; final investment is shaped by creative direction, scope, and delivery. Creative direction, posing guidance, styling support, and gallery delivery are included.
                </p>
            </Section>

            <Section spacing="normal">
                <div className="space-y-16">
                    {PACKAGES.map((p, i) => (
                        <article
                            key={p.id}
                            data-testid={`package-card-${p.id}`}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
                        >
                            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                <EditorialImage src={p.image} alt={p.name} ratio={4 / 5} />
                            </div>
                            <div className="lg:col-span-6">
                                <div className="text-[11px] text-muted-foreground tracking-[0.18em] uppercase">
                                    0{i + 1} · {p.ideal.split(",")[0]}
                                </div>
                                <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">{p.name}</h2>
                                <Separator className="my-5 bg-[hsl(var(--lux-brass))]/40 w-10" />
                                <p className="text-sm text-muted-foreground">Ideal for: {p.ideal}</p>
                                <div className="mt-5 text-sm leading-[1.9]">
                                    <div className="kicker mb-2">What's included</div>
                                    <ul className="space-y-2">
                                        {p.includes.map((it) => (
                                            <li key={it} className="flex items-start gap-3">
                                                <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0" />
                                                <span>{it}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-5">
                                    <div className="text-xs tracking-[0.16em] uppercase text-muted-foreground">
                                        Starting at <span className="text-foreground ml-1 font-serif-display text-xl normal-case tracking-normal">{p.starting}</span>
                                    </div>
                                    <Link
                                        to={`/inquire?type=${encodeURIComponent(p.name)}`}
                                        data-testid={`package-inquire-button-${p.id}`}
                                        className="btn-request btn-request--sm"
                                    >
                                        Request Availability
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </Section>

            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border text-center">
                <Kicker>How the process works</Kicker>
                <SerifTitle className="mt-3 max-w-[24ch] mx-auto">From inquiry to gallery, every step is held.</SerifTitle>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
                    {[
                        { n: "01", t: "Inquire", c: "Share your vision through a short, considered form." },
                        { n: "02", t: "Concept", c: "A private call, a moodboard, and an intentional plan." },
                        { n: "03", t: "Session", c: "Calm, guided direction, styling support, and the photographs." },
                        { n: "04", t: "Gallery", c: "A retouched, private gallery delivered with care." },
                    ].map((s) => (
                        <div key={s.n} className="bg-background border border-border rounded-[2px] p-6">
                            <div className="kicker">{s.n}</div>
                            <div className="font-serif-display text-xl mt-2">{s.t}</div>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/40 w-8" />
                            <p className="text-sm text-muted-foreground">{s.c}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-12 text-xs text-muted-foreground">
                    A limited number of sessions are accepted each month to preserve a high‑touch client experience.
                </p>
            </Section>
        </div>
    );
}
